import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

const projects = [
  {
    title: "E-Commerce System",
    tech: "Laravel + MySQL",
    overview:
      "A full-featured e-commerce platform with product management, cart, and order processing.",
    features: [
      "Product & category management",
      "User authentication",
      "Shopping cart & checkout",
      "Admin dashboard",
    ],
    status: "Completed",
  },
  {
    title: "E-Learning Website",
    tech: "React + Tailwind CSS",
    overview:
      "An online learning platform designed to provide courses, educational content, and user interaction through a modern UI.",
    features: [
      "Course listing & details",
      "Responsive UI",
      "Student-friendly design",
      "Contact & enquiry forms",
    ],
    status: "Completed",
  },
  {
    title: "Travel Management System",
    tech: "Java + Spring Boot",
    overview:
      "A management system to handle trips, drivers, vehicles, and travel records efficiently.",
    features: [
      "Trip assignment & tracking",
      "Driver & vehicle management",
      "Admin dashboard",
      "Database-backed reports",
    ],
    status: "Completed",
  },
  {
    title: "ERP Software",
    tech: "Laravel + MySQL",
    overview:
      "An ERP system developed to manage internal business operations and centralized data flow.",
    features: [
      "Role-based access control",
      "Centralized data management",
      "Reporting modules",
      "Scalable system architecture",
    ],
    status: "In Progress",
  },
];

export default function Portfolio() {
  const [selectedProject, setSelectedProject] = useState(null);

  return (
    <section className="min-h-screen py-24 px-4 bg-black text-white">
      <h2 className="text-4xl font-bold text-center mb-12">My Work</h2>

      <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-8">
        {projects.map((p, i) => (
          <div
            key={i}
            onClick={() => setSelectedProject(p)}
            className="cursor-pointer bg-white/5 border border-white/10 rounded-2xl p-6 hover:scale-105 transition"
          >
            <h3 className="text-xl font-semibold mb-2">{p.title}</h3>
            <p className="text-gray-400">{p.tech}</p>
            <span className="text-green-400 mt-4 inline-block">
              View Project →
            </span>
          </div>
        ))}
      </div>

      {/* Animated Modal */}
      <AnimatePresence>
        {selectedProject && (
          <motion.div
            className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 px-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setSelectedProject(null)}
          >
            <motion.div
              className="bg-gray-900 max-w-lg w-full rounded-xl p-6 relative"
              initial={{ scale: 0.85, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.85, opacity: 0 }}
              transition={{ duration: 0.25, ease: "easeOut" }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Close */}
              <button
                className="absolute top-3 right-3 text-gray-400 hover:text-white"
                onClick={() => setSelectedProject(null)}
              >
                ✕
              </button>

              <h3 className="text-2xl font-bold mb-2 text-green-400">
                {selectedProject.title}
              </h3>
              <p className="text-sm text-gray-400 mb-4">
                {selectedProject.tech}
              </p>

              <div className="mb-4">
                <h4 className="font-semibold mb-1">Project Overview</h4>
                <p className="text-gray-300 text-sm">
                  {selectedProject.overview}
                </p>
              </div>

              <div className="mb-4">
                <h4 className="font-semibold mb-1">Features</h4>
                <ul className="list-disc list-inside text-gray-300 text-sm space-y-1">
                  {selectedProject.features.map((f, i) => (
                    <li key={i}>{f}</li>
                  ))}
                </ul>
              </div>

              <div>
                <h4 className="font-semibold mb-1">Status</h4>
                <span className="inline-block px-3 py-1 text-sm rounded-full bg-green-500/10 text-green-400">
                  {selectedProject.status}
                </span>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
